import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Grid,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { AddGameDialog } from './dialogs/AddGameDialog';
import { EditGameDialog } from './dialogs/EditGameDialog';
import { 
  getPromotionData, 
  removeGameFromPromotion, 
  setPromotionPriority,
  updateGameDisplayOrder 
} from '../../Provider/parseDataProvider';

export const GamePromotion = () => {
  const [prioritySection, setPrioritySection] = useState(''); // '' | 'newLaunch' | 'comingSoon'
  const [pendingPrioritySection, setPendingPrioritySection] = useState(''); // For unsaved changes
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingPriority, setSavingPriority] = useState(false);
  const [error, setError] = useState(null);
  
  // Real data from API
  const [newLaunchGames, setNewLaunchGames] = useState([]);
  const [comingSoonImage, setComingSoonImage] = useState(null);

  // Load promotion data on component mount
  useEffect(() => {
    loadPromotionData();
  }, []);

  const loadPromotionData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getPromotionData();
      
      if (response.success) {
        const { prioritySection: currentPriority, newLaunchGames, comingSoonImage } = response.data;
        const priority = currentPriority || '';
        setPrioritySection(priority);
        setPendingPrioritySection(priority); // Initialize pending state
        setNewLaunchGames(newLaunchGames || []);
        setComingSoonImage(comingSoonImage || null);
      } else {
        setError(response.message || 'Failed to load promotion data');
      }
    } catch (err) {
      console.error('Error loading promotion data:', err);
      setError('Failed to load promotion data');
    } finally {
      setLoading(false);
    }
  };

  const handlePriorityChange = (section) => {
    // Update pending state only, don't call API yet
    const newPriority = pendingPrioritySection === section ? '' : section;
    setPendingPrioritySection(newPriority);
  };

  const handleSavePriority = async () => {
    try {
      setSavingPriority(true);
      setError(null);
      
      const response = await setPromotionPriority(pendingPrioritySection);
      
      if (response.success) {
        setPrioritySection(pendingPrioritySection);
      } else {
        setError(response.message || 'Failed to update priority');
      }
    } catch (err) {
      console.error('Error updating priority:', err);
      setError('Failed to update priority');
    } finally {
      setSavingPriority(false);
    }
  };

  // Check if there are unsaved priority changes
  const hasUnsavedChanges = pendingPrioritySection !== prioritySection;

  const handleAddGame = async (gameData) => {
    try {
      setError(null);
      
      // The AddGameDialog now handles the image upload internally
      // So we just need to reload the data
      await loadPromotionData();
      setOpenAddDialog(false);
    } catch (err) {
      console.error('Error adding game:', err);
      setError('Failed to add game');
    }
  };

  const handleEditGame = (game, section) => {
    setEditingGame({
      ...game,
      section: section,
      popUpImage: game.popUpImage || null
    });
    setOpenEditDialog(true);
  };

  const handleEditSubmit = async (gameData) => {
    try {
      setError(null);
      
      // The EditGameDialog handles the update internally
      // So we just need to reload the data
      await loadPromotionData();
      setOpenEditDialog(false);
    } catch (err) {
      console.error('Error updating game:', err);
      setError('Failed to update game');
    }
  };

  const handleDeleteGame = async (gameId, section) => {
    try {
      setError(null);
      
      const response = await removeGameFromPromotion(gameId, section);
      
      if (response.success) {
        // Update local state immediately for better UX
        if (section === 'newLaunch') {
          setNewLaunchGames(newLaunchGames.filter(game => game.id !== gameId));
        } else {
          // For Coming Soon, clear the image
          setComingSoonImage(null);
        }
      } else {
        setError(response.message || 'Failed to remove game');
      }
    } catch (err) {
      console.error('Error removing game:', err);
      setError('Failed to remove game');
    }
  };

  // Move game up in the list
  const handleMoveUp = async (index) => {
    if (index === 0) return; // Already at top

    try {
      setError(null);
      
      // Create a copy of the games array
      const updatedGames = [...newLaunchGames];
      
      // Swap with previous item
      [updatedGames[index - 1], updatedGames[index]] = [updatedGames[index], updatedGames[index - 1]];
      
      // Update local state immediately for better UX
      setNewLaunchGames(updatedGames);
      
      // Prepare the order data with new displayOrder values (1-indexed)
      const orderData = updatedGames.map((game, idx) => ({
        gameId: game.id,
        displayOrder: idx + 1
      }));
      
      // Call API to update the order in the database
      const response = await updateGameDisplayOrder(orderData);
      
      if (!response.success) {
        // Revert on failure
        await loadPromotionData();
        setError(response.message || 'Failed to update game order');
      }
    } catch (err) {
      console.error('Error updating game order:', err);
      setError('Failed to update game order');
      // Reload data on error
      await loadPromotionData();
    }
  };

  // Move game down in the list
  const handleMoveDown = async (index) => {
    if (index === newLaunchGames.length - 1) return; // Already at bottom

    try {
      setError(null);
      
      // Create a copy of the games array
      const updatedGames = [...newLaunchGames];
      
      // Swap with next item
      [updatedGames[index], updatedGames[index + 1]] = [updatedGames[index + 1], updatedGames[index]];
      
      // Update local state immediately for better UX
      setNewLaunchGames(updatedGames);
      
      // Prepare the order data with new displayOrder values (1-indexed)
      const orderData = updatedGames.map((game, idx) => ({
        gameId: game.id,
        displayOrder: idx + 1
      }));
      
      // Call API to update the order in the database
      const response = await updateGameDisplayOrder(orderData);
      
      if (!response.success) {
        // Revert on failure
        await loadPromotionData();
        setError(response.message || 'Failed to update game order');
      }
    } catch (err) {
      console.error('Error updating game order:', err);
      setError('Failed to update game order');
      // Reload data on error
      await loadPromotionData();
    }
  };

  const GameItem = ({ game, section, index, totalGames, showOrderButtons = false }) => (
    <Box 
      sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        py: 1.5,
        px: 2,
        mb: 1,
        backgroundColor: '#2a2a2a',
        borderRadius: 1,
        border: prioritySection === section ? '2px solid #ffc107' : '1px solid #444',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: '#333',
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {showOrderButtons && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <IconButton 
              size="small" 
              onClick={() => handleMoveUp(index)}
              disabled={index === 0}
              sx={{ 
                color: index === 0 ? '#555' : '#4caf50',
                padding: '2px',
                '&:hover': {
                  backgroundColor: index === 0 ? 'transparent' : 'rgba(76, 175, 80, 0.1)',
                },
                '&:disabled': {
                  color: '#555'
                }
              }}
            >
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => handleMoveDown(index)}
              disabled={index === totalGames - 1}
              sx={{ 
                color: index === totalGames - 1 ? '#555' : '#4caf50',
                padding: '2px',
                '&:hover': {
                  backgroundColor: index === totalGames - 1 ? 'transparent' : 'rgba(76, 175, 80, 0.1)',
                },
                '&:disabled': {
                  color: '#555'
                }
              }}
            >
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        <Typography variant="body1" sx={{ fontWeight: 500, color: '#fff' }}>
          {game.name}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <IconButton 
          size="small" 
          onClick={() => handleEditGame(game, section)}
          sx={{ 
            color: '#ffc107',
            '&:hover': {
              backgroundColor: 'rgba(255, 193, 7, 0.1)',
              color: '#ffc107'
            }
          }}
        >
          <EditIcon />
        </IconButton>
        <IconButton 
          size="small" 
          onClick={() => handleDeleteGame(game.id, section)}
          sx={{ 
            color: '#f44336',
            '&:hover': {
              backgroundColor: 'rgba(244, 67, 54, 0.1)',
              color: '#f44336'
            }
          }}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ p: 3, backgroundColor: '#1a1a1a', minHeight: '100vh' }}>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress sx={{ color: '#ffc107' }} />
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            {/* Current Priority Display */}
            <Typography variant="h6" sx={{ color: '#ffc107', fontWeight: 600 }}>
              Current Priority: {prioritySection === '' ? 'None' : 
                prioritySection === 'newLaunch' ? 'New Launch Games' : 'Coming Soon'}
            </Typography>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {hasUnsavedChanges && (
                <Button
                  variant="contained"
                  onClick={handleSavePriority}
                  disabled={savingPriority}
                  sx={{
                    background: "#ffc107",
                    color: "#000",
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "#FFA000",
                    },
                    "&:disabled": {
                      backgroundColor: "#666",
                      color: "#999"
                    }
                  }}
                >
                  {savingPriority ? <CircularProgress size={20} sx={{ color: '#999' }} /> : 'Save Priority'}
                </Button>
              )}
              
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenAddDialog(true)}
                sx={{
                  background: "#FFC107",
                  color: "#0A0A0A",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: "#FFA000",
                  },
                }}
              >
                ADD GAME
              </Button>
            </Box>
          </Box>

          <Grid container spacing={3}>
            {/* New Launch Games Section */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, backgroundColor: '#2a2a2a', boxShadow: 3, border: '1px solid #444' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5" component="h2" sx={{ color: '#fff', fontWeight: 500 }}>
                    New Launch Games
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={pendingPrioritySection === 'newLaunch'}
                        onChange={() => handlePriorityChange('newLaunch')}
                        sx={{ 
                          color: '#ffc107',
                          '&.Mui-checked': {
                            color: '#ffc107',
                          }
                        }}
                      />
                    }
                    label="Priority"
                    sx={{ '& .MuiFormControlLabel-label': { color: '#ccc' } }}
                  />
                </Box>
                
                <Box>
                  {newLaunchGames.length > 0 ? (
                    newLaunchGames.map((game, index) => (
                      <GameItem 
                        key={game.id} 
                        game={game} 
                        section="newLaunch" 
                        index={index}
                        totalGames={newLaunchGames.length}
                        showOrderButtons={true}
                      />
                    ))
                  ) : (
                    <Typography variant="body2" sx={{ color: '#888', textAlign: 'center', py: 4 }}>
                      No new launch games added yet
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Coming Soon Games Section */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, backgroundColor: '#2a2a2a', boxShadow: 3, border: '1px solid #444' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5" component="h2" sx={{ color: '#fff', fontWeight: 500 }}>
                    Coming Soon
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={pendingPrioritySection === 'comingSoon'}
                        onChange={() => handlePriorityChange('comingSoon')}
                        sx={{ 
                          color: '#ffc107',
                          '&.Mui-checked': {
                            color: '#ffc107',
                          }
                        }}
                      />
                    }
                    label="Priority"
                    sx={{ '& .MuiFormControlLabel-label': { color: '#ccc' } }}
                  />
                </Box>
                
                <Box>
                  {comingSoonImage ? (
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        p: 2,
                        backgroundColor: '#3a3a3a',
                        borderRadius: '8px',
                        border: '1px solid #555'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <img
                          src={comingSoonImage}
                          alt="Coming Soon"
                          style={{
                            width: '60px',
                            height: '60px',
                            borderRadius: '8px',
                            objectFit: 'cover',
                            border: '1px solid #ddd'
                          }}
                        />
                        <Typography variant="body1" sx={{ color: '#fff', fontWeight: 500 }}>
                          Coming Soon Image
                        </Typography>
                      </Box>
                      <Box>
                        <IconButton
                          onClick={() => handleEditGame({ 
                            id: 'comingSoon', 
                            name: 'Coming Soon', 
                            popUpImage: comingSoonImage 
                          }, 'comingSoon')}
                          sx={{ color: '#ffc107', mr: 1 }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteGame('comingSoon', 'comingSoon')}
                          sx={{ color: '#f44336' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: '#888', textAlign: 'center', py: 4 }}>
                      No coming soon image added yet
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      {/* Add Game Dialog */}
      <AddGameDialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        onSubmit={handleAddGame}
      />

      {/* Edit Game Dialog */}
      <EditGameDialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        onSubmit={handleEditSubmit}
        gameData={editingGame}
      />
    </Box>
  );
};